import React, { useState, useRef, useEffect } from 'react';
import { useEVM } from '../contexts/EVMProvider';
import { ChevronDown, ChevronUp, Check, Coins } from 'lucide-react';

const ChainSelector = () => {
  const dropdownRef = useRef(null);
  const {
    selectedEVMChain,
    setSelectedEVMChain,
    EVM_CHAINS,
    switchEVMChain,
    evmProvider,
  } = useEVM();

  const [isOpen, setIsOpen] = useState(false);
  const [showEVMChains, setShowEVMChains] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowEVMChains(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEVMChainSelect = async (chainId) => {
    console.log('Selecting EVM chain:', chainId);
    if (evmProvider) {
      // If wallet is connected, switch the chain
      try {
        await switchEVMChain(chainId);
      } catch (error) {
        console.error('Failed to switch chain:', error);
        // Still update the selection even if switch fails
        setSelectedEVMChain(chainId);
      }
    } else {
      // If wallet not connected, just update selection
      setSelectedEVMChain(chainId);
    }
    setShowEVMChains(false);
  };

  const getCurrentChainInfo = () => {
      const chain = EVM_CHAINS[selectedEVMChain];
      return {
        name: chain?.name || 'Ethereum',
        icon: <Coins className="w-4 h-4" />,
      color: 'text-primary-400',
      };
  };

  const currentChain = getCurrentChainInfo();

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 sm:space-x-2 glass hover:shadow-glow border border-white/20 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
      >
        <div className={currentChain.color}>
            <Coins className="w-3 h-3 sm:w-4 sm:h-4" />
        </div>
        <span className="text-white hidden sm:inline">{currentChain.name}</span>
        <span className="text-white sm:hidden">EVM</span>
        {isOpen ? (
          <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4 text-white/60" />
        ) : (
          <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-white/60" />
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 sm:w-72 glass-dark border border-white/20 rounded-lg shadow-glow-lg z-[9999] max-h-80 sm:max-h-96 overflow-y-auto">
          <div className="p-2">
            {/* EVM Chains */}
              <div className="mt-3 pt-3 border-t border-gray-600">
                <button
                  onClick={() => setShowEVMChains(!showEVMChains)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-white/60 hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                >
                <span>EVM Chains</span>
                  {showEVMChains ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                
                {showEVMChains && (
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {Object.entries(EVM_CHAINS).map(([chainId, chain]) => (
                      <button
                        key={chainId}
                        onClick={() => handleEVMChainSelect(parseInt(chainId))}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                          selectedEVMChain === parseInt(chainId)
                          ? 'bg-primary-500/30 text-primary-300 border border-primary-500/50'
                          : 'hover:bg-white/10 text-white/80'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Coins className="w-4 h-4" />
                          <span>{chain.name}</span>
                        </div>
                        {selectedEVMChain === parseInt(chainId) && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChainSelector; 
